import React, { useCallback } from 'react'
import { cn, Checkbox, TextButton, RectangleButton, Dialog } from '@yanolja-rnd/nol-design-system'
import type { CheckboxChangeEvent } from '@yanolja-rnd/nol-design-system'
import { useInView } from 'react-intersection-observer'
import useDomesticCartOptions from '~/app/components-nds/domestic/cart/hooks/use-domestic-cart-options'
import useDomesticCartDelete from '~/app/components-nds/domestic/cart/hooks/use-domestic-cart-delete'
import useDomesticCartStore from '~/app/store/domestic/cart/cart'
import useModalHashV2 from '~/app/components-nds/hooks/use-modal-hash'
import { CartPageLog } from '~/app/kinesis/protobuf/clientlogs/order-misc-common-txtpb'
import { zIndexMax } from '~/app/components/styles/units'
import useIsPcWidth from '~/app/utils/useIsPcWidth'

/**
 * CartSelectBar Component
 * 장바구니 상품 전체 선택 및 선택 삭제 기능을 제공하는 컴포넌트
 */
const CartSelectBar = () => {
  // 이 지면 PC 레이아웃은 pf-pc(1024)부터 시작 — JS 분기점을 CSS `pf-pc:` 와 일치시킨다
  const { isPcWidth } = useIsPcWidth(1024)
  const responsiveButtonSize = isPcWidth ? 'medium' : 'small'

  /** 선택한 상품 삭제 확인 다이얼로그 표시 여부 */
  const [isSelectedItemDeleteDialogOpen, setSelectedItemDeleteDialogOpen] = useModalHashV2({
    id: 'remove-selected-confirm-dialog',
  })
  /** 구매불가 상품 삭제 확인 다이얼로그 표시 여부 */
  const [isUnavailableItemDeleteDialogOpen, setUnavailableItemDeleteDialogOpen] = useModalHashV2({
    id: 'remove-unavailable-confirm-dialog',
  })
  const { deleteCartOptions } = useDomesticCartDelete()

  const { unavailableOptionIds, hasUnavailableOptions, allAvailableOptionIds, totalOptionCount } =
    useDomesticCartOptions()

  const { selectedOptionIds, selectedOptionCount, selectAllOptions, unselectAllOptions } =
    useDomesticCartStore(({ selectedOptionIds, selectAllOptions, unselectAllOptions }) => {
      return {
        selectedOptionIds,
        selectedOptionCount: selectedOptionIds.size,
        selectAllOptions,
        unselectAllOptions,
      }
    })

  const isAllSelected = totalOptionCount > 0 && selectedOptionCount === totalOptionCount

  const handleCheckboxChange = useCallback(
    (event: CheckboxChangeEvent) => {
      if (event.target.checked) {
        selectAllOptions(allAvailableOptionIds ?? [])
      } else {
        unselectAllOptions()
      }
    },
    [selectAllOptions, unselectAllOptions, allAvailableOptionIds]
  )

  const onRemoveSelected = useCallback(() => {
    if (selectedOptionCount > 0) {
      setSelectedItemDeleteDialogOpen(true)
    }
  }, [selectedOptionCount, setSelectedItemDeleteDialogOpen])

  const executeRemoveSelected = useCallback(() => {
    setSelectedItemDeleteDialogOpen(false)
    const optionIdsToRemove = Array.from(selectedOptionIds)

    deleteCartOptions(optionIdsToRemove)
  }, [selectedOptionIds, deleteCartOptions, setSelectedItemDeleteDialogOpen])

  const onRemoveUnavailable = useCallback(() => {
    if (hasUnavailableOptions) {
      setUnavailableItemDeleteDialogOpen(true)
    }
    CartPageLog.ButtonClickName.click({ clickName: 'CART_CLICK_TYPE_CART_DEL' })
  }, [hasUnavailableOptions, setUnavailableItemDeleteDialogOpen])

  const executeRemoveUnavailable = useCallback(() => {
    deleteCartOptions(unavailableOptionIds, {
      onSettled: () => {
        setUnavailableItemDeleteDialogOpen(false)
      },
    })
  }, [unavailableOptionIds, deleteCartOptions, setUnavailableItemDeleteDialogOpen])

  // 스크롤 위치 감지를 위한 ref와 inView 상태
  const { ref: topSentinelRef, inView: isTop } = useInView({
    initialInView: true,
  })

  return (
    <>
      {/* 스크롤 위치 감지를 위한 요소 */}
      <div ref={topSentinelRef} className="absolute top-0 h-0 w-full" />

      <div
        className={cn(
          // 모바일: 헤더 아래 고정 + 풀블리드 배경 / PC: 본문 내 정적 (배경·모서리는 안쪽 카드가 담당)
          'fixed inset-x-0 top-52 z-30 bg-static-white pf-pc:static pf-pc:top-auto pf-pc:bg-transparent',
          {
            'shadow-[0px_6px_8px_-6px_rgba(0,0,0,0.2)] pf-pc:shadow-none': !isTop,
          }
        )}
      >
        {/* 안쪽: pf-pc 미만은 풀폭(헤더와 동일 full-bleed) */}
        <div className="mx-auto flex w-full max-w-pf-pc items-center justify-between gap-8 px-16 py-10 pf-pc:max-w-none pf-pc:rounded-16 pf-pc:bg-static-white pf-pc:px-24 pf-pc:py-16">
          <div className="flex items-center gap-8">
            <Checkbox
              checked={isAllSelected}
              onChange={handleCheckboxChange}
              aria-label="전체 선택"
              size={responsiveButtonSize}
            />
            <div className="flex items-center gap-6">
              <div className="text-text-neutral-main typography-body-14-regular">전체선택</div>
              <div className="text-text-neutral-main typography-body-14-bold">
                {selectedOptionCount}/{totalOptionCount}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-12">
            {hasUnavailableOptions && (
              <TextButton
                theme="underline"
                mobileSize={responsiveButtonSize}
                pcSize={responsiveButtonSize}
                weight="regular"
                onClick={onRemoveUnavailable}
              >
                구매불가 삭제
              </TextButton>
            )}

            <RectangleButton
              theme="outlined_basic"
              size={responsiveButtonSize}
              weight="regular"
              onClick={onRemoveSelected}
              aria-label="선택 삭제"
              disabled={selectedOptionCount === 0}
            >
              선택 삭제
            </RectangleButton>
          </div>
        </div>
      </div>

      <div className="h-54 pf-pc:h-0" />
      {/** 선택 삭제 확인 다이얼로그 */}
      <Dialog
        id="remove-selected-confirm-dialog"
        show={isSelectedItemDeleteDialogOpen}
        title={
          selectedOptionCount === 1
            ? '선택한 상품을 삭제할까요?'
            : `선택한 ${selectedOptionCount}개의 상품을 삭제할까요?`
        }
        negativeButton={{
          text: '아니요',
          onClick: () => setSelectedItemDeleteDialogOpen(false),
        }}
        positiveButton={{
          text: '삭제하기',
          onClick: executeRemoveSelected,
        }}
        zIndex={zIndexMax}
      />

      {/** 구매불가 삭제 확인 다이얼로그 */}
      <Dialog
        id="remove-unavailable-confirm-dialog"
        show={isUnavailableItemDeleteDialogOpen}
        title="구매할 수 없는 상품을 모두 삭제할까요?"
        negativeButton={{
          text: '아니요',
          onClick: () => setUnavailableItemDeleteDialogOpen(false),
        }}
        positiveButton={{ text: '삭제하기', onClick: executeRemoveUnavailable }}
        zIndex={zIndexMax}
      />
    </>
  )
}

export default CartSelectBar
